import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Quantity-tier upsell for the product page ("1 band / 2 bands / 3 bands", then split the
 * quantity across colorways). Adds every chosen variant in one request and hands the result to
 * the cart drawer the same way Horizon's product form does.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} tiers
 * @property {HTMLElement[]} rows
 * @property {HTMLElement} total
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} hint
 * @property {HTMLElement} [progressFill]
 * @property {HTMLElement[]} [steps]
 * @property {HTMLElement} [error]
 *
 * @extends Component<Refs>
 */
class ClaspLabBundle extends Component {
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#applyTier(this.#tierIndex(), true);
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
  }

  get #threshold() {
    return Number(this.dataset.freeShippingCents || 0);
  }

  /** @returns {number} */
  #tierIndex() {
    const { tiers = [] } = this.refs;
    const i = tiers.findIndex((t) => t.checked);
    return i === -1 ? 0 : i;
  }

  /** @returns {{ qty: number, free: number, label: string }} */
  #tier(index = this.#tierIndex()) {
    const input = this.refs.tiers?.[index];
    return {
      qty: Number(input?.dataset.qty || 1),
      free: Number(input?.dataset.free || 0),
      label: input?.dataset.label || '',
    };
  }

  /** @param {HTMLElement} row */
  #rowQty(row) {
    return Number(row.dataset.qty || 0);
  }

  /** @param {HTMLElement} row @param {number} qty */
  #setRowQty(row, qty) {
    row.dataset.qty = String(Math.max(0, qty));
    const out = row.querySelector('[data-row-qty]');
    if (out) out.textContent = row.dataset.qty;
    row.classList.toggle('cl-bundle__row--active', qty > 0);
  }

  #allocated() {
    return (this.refs.rows || []).reduce((sum, row) => sum + this.#rowQty(row), 0);
  }

  /** Declarative: on:change="/selectTier" on each radio. */
  selectTier() {
    this.#applyTier(this.#tierIndex());
  }

  /** @param {number} index @param {boolean} [initial] */
  #applyTier(index, initial = false) {
    const { tiers = [], rows = [] } = this.refs;
    tiers.forEach((t, i) => t.closest('.cl-tier')?.classList.toggle('cl-tier--selected', i === index));
    const { qty } = this.#tier(index);
    const available = rows.filter((r) => !r.hasAttribute('data-unavailable'));
    // Keep the current split, then fill or trim to the tier quantity.
    let total = this.#allocated();
    if (initial || total === 0) {
      rows.forEach((r) => this.#setRowQty(r, 0));
      if (available[0]) this.#setRowQty(available[0], qty);
    } else if (total < qty) {
      const first = available.find((r) => this.#rowQty(r) > 0) || available[0];
      if (first) this.#setRowQty(first, this.#rowQty(first) + (qty - total));
    } else if (total > qty) {
      for (const r of [...rows].reverse()) {
        if (total <= qty) break;
        const take = Math.min(this.#rowQty(r), total - qty);
        this.#setRowQty(r, this.#rowQty(r) - take);
        total -= take;
      }
    }
    this.#render();
  }

  /** on:click="/increment/{index}" */
  increment(index) {
    const { rows = [] } = this.refs;
    const row = rows[Number(index)];
    if (!row || row.hasAttribute('data-unavailable')) return;
    const { qty } = this.#tier();
    if (this.#allocated() >= qty) {
      // Take one from another colorway so the total stays at the tier quantity.
      const donor = [...rows].reverse().find((r) => r !== row && this.#rowQty(r) > 0);
      if (!donor) return;
      this.#setRowQty(donor, this.#rowQty(donor) - 1);
    }
    this.#setRowQty(row, this.#rowQty(row) + 1);
    this.#render();
  }

  /** on:click="/decrement/{index}" */
  decrement(index) {
    const row = this.refs.rows?.[Number(index)];
    if (!row || this.#rowQty(row) === 0) return;
    this.#setRowQty(row, this.#rowQty(row) - 1);
    this.#render();
  }

  #totalCents() {
    return (this.refs.rows || []).reduce((sum, row) => sum + this.#rowQty(row) * Number(row.dataset.price || 0), 0);
  }

  #render() {
    const { total, submit, hint, progressFill, steps = [], tiers = [] } = this.refs;
    const tier = this.#tier();
    const allocated = this.#allocated();
    const missing = tier.qty - allocated;
    const unitCents = Number(this.dataset.unitPrice || 0);
    const payCents = this.#totalCents() - tier.free * unitCents;

    if (total) total.textContent = this.#money(Math.max(0, payCents));
    if (submit) submit.disabled = missing > 0 || this.#busy;

    if (hint) {
      const pickMore = this.dataset.textPickMore || 'Pick {count} more.';
      const toShipping = this.dataset.textToShipping || 'Add {amount} more for free shipping.';
      const nextTier = this.dataset.textNextTier || 'Next: {label}.';
      const freeShipping = this.dataset.textFreeShipping || 'Free shipping included.';
      if (missing > 0) {
        hint.textContent = pickMore.replace('{count}', String(missing));
      } else if (this.#threshold && payCents < this.#threshold) {
        hint.textContent = toShipping.replace('{amount}', this.#money(this.#threshold - payCents));
      } else if (this.#threshold) {
        hint.textContent = freeShipping;
      } else {
        const next = this.#tier(this.#tierIndex() + 1);
        hint.textContent = tiers[this.#tierIndex() + 1] ? nextTier.replace('{label}', next.label) : '';
      }
    }

    const index = this.#tierIndex();
    if (progressFill && tiers.length > 1) progressFill.style.width = `${(index / (tiers.length - 1)) * 100}%`;
    steps.forEach((s, i) => s.classList.toggle('cl-bundle__step--done', i <= index));
  }

  /** on:click="/add" */
  async add() {
    if (this.#busy) return;
    const { rows = [], submit, error } = this.refs;
    const items = rows
      .filter((r) => this.#rowQty(r) > 0)
      .map((r) => ({ id: Number(r.dataset.variantId), quantity: this.#rowQty(r) }));
    if (!items.length) return;

    this.#busy = true;
    if (submit) submit.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      const cart = await this.#fetchCart();
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cl-bundle-component',
          sourceId: this.id || 'cl-bundle',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          productId: this.dataset.productId,
          sections: response.sections,
          didError,
        },
      });
      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (error) {
          error.textContent = response.description || response.message || 'Could not add to cart.';
          error.hidden = false;
        }
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      if (error) {
        error.textContent = 'Could not add to cart. Please try again.';
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      if (submit) submit.removeAttribute('aria-busy');
      this.#render();
    }
  }

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof (/** @type {any} */ (cartItems)).fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItems).fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.#moneyFormat;
    const value = cents / 100;
    const fixed = value.toFixed(2);
    const [int, dec] = fixed.split('.');
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const map = {
      amount: `${group(int, ',')}.${dec}`,
      amount_no_decimals: group(Math.round(value).toString(), ','),
      amount_with_comma_separator: `${group(int, '.')},${dec}`,
      amount_no_decimals_with_comma_separator: group(Math.round(value).toString(), '.'),
      amount_with_space_separator: `${group(int, ' ')},${dec}`,
      amount_no_decimals_with_space_separator: group(Math.round(value).toString(), ' '),
      amount_with_apostrophe_separator: `${group(int, "'")}.${dec}`,
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => map[key] ?? map.amount);
  }
}

if (!customElements.get('cl-bundle-component')) customElements.define('cl-bundle-component', ClaspLabBundle);
